import {
  FormField,
  FormGrid,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useEffect, useMemo } from 'react';
import {
  Controller,
  ControllerProps,
  FieldValues,
  Path,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';

import { HookFormSelectionList } from 'components/hookForm';
import { useBreakpointValue } from 'utils/mediaQuery';

import { AuthorizationHeader } from '../../../../../components/authorizationHeader/authorizationHeader';
import type { AuthorizationType } from '../../../../../components/authorizationHeader/authorizationHeader.types';
import { CustomHeaders } from '../../../../../components/customHeaders/customHeaders';
import { OAuth } from '../../../../../components/oAuth/oAuth';
import {
  getEmptyInputMessage,
  isValidateCustomHeaders,
  thisFieldIsEmptyMessage,
} from '../../../../../utils/validateMessages';

type AuthorizationMethodType = 'authorizationHeader' | 'oAuth';

type AuthorizationMethod = {
  readonly id: AuthorizationMethodType;
  readonly label: string;
};

const authorizationMethods: Record<
  AuthorizationMethodType,
  AuthorizationMethod
> = {
  authorizationHeader: {
    id: 'authorizationHeader',
    label: 'Authorization header',
  },
  oAuth: { id: 'oAuth', label: 'OAuth' },
};

type RequestHeadersProps<TFieldValues extends FieldValues> = {
  control: ControllerProps<TFieldValues>['control'];
  className: string;
  selectedAuthorizationType: AuthorizationType;
  setSelectedAuthorizationType: (type: AuthorizationType) => void;
  disabled: boolean;
  handleCustomCardSubmit: () => void;
};

export const RequestHeaders = <TFieldValues extends FieldValues>({
  control,
  className,
  selectedAuthorizationType,
  setSelectedAuthorizationType,
  disabled,
  handleCustomCardSubmit,
}: RequestHeadersProps<TFieldValues>): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const authorizationMethodSelectItems: readonly AuthorizationMethod[] =
    useMemo(
      () =>
        Object.keys(authorizationMethods).reduce(
          (acc, key) => [
            ...acc,
            authorizationMethods[key as AuthorizationMethodType],
          ],
          [] as readonly AuthorizationMethod[],
        ),
      [],
    );

  const {
    field: { onChange: onChangeOAuthStrategy },
  } = useController({
    control,
    name: 'oAuthStrategy' as Path<TFieldValues>,
  });

  const authorizationMethodForm = useForm<{
    selectedAuthorizationMethod: AuthorizationMethod[];
  }>({
    defaultValues: { selectedAuthorizationMethod: [] },
  });

  const [selectedAuthorizationMethod] = useWatch({
    control: authorizationMethodForm.control,
    name: 'selectedAuthorizationMethod',
  });

  const [requestOAuthId, customCardAuthorizationHeader] = useWatch({
    control,
    name: [
      'request.o_auth_connection_id',
      'authorizationHeader',
    ] as Path<TFieldValues>[],
  });

  // Setting the authorization method form if there is already an authorization
  // header or oAuth id in the custom card
  useEffect(() => {
    if (customCardAuthorizationHeader) {
      authorizationMethodForm.reset({
        selectedAuthorizationMethod: [authorizationMethods.authorizationHeader],
      });
    } else if (requestOAuthId) {
      authorizationMethodForm.reset({
        selectedAuthorizationMethod: [authorizationMethods.oAuth],
      });
    }
  }, [customCardAuthorizationHeader, requestOAuthId, authorizationMethodForm]);

  return (
    <FormGrid className={className}>
      <FormField span={isSmallScreen ? 6 : 3}>
        <HookFormSelectionList
          label="Authorization method"
          name={'selectedAuthorizationMethod'}
          control={authorizationMethodForm.control}
          disabled={disabled}
          componentProps={{
            multiple: false,
            clearable: true,
            placeholder: 'Select an authorization method',
            getItemTitleByValue: (
              method: AuthorizationMethod | null | undefined,
            ) => <Text.Paragraph>{method?.label}</Text.Paragraph>,
            equalityFn: (item1, item2) => item1.id === item2.id,
          }}
        >
          {authorizationMethodSelectItems.map((method) => (
            <SelectionList.Item key={method.id} value={method}>
              {method.label}
            </SelectionList.Item>
          ))}
        </HookFormSelectionList>
      </FormField>

      {selectedAuthorizationMethod?.id === 'authorizationHeader' && (
        <>
          <FormField>
            <Controller
              name={'authorizationHeader' as Path<TFieldValues>}
              control={control}
              rules={{
                required: {
                  value: selectedAuthorizationType !== 'none',
                  message: thisFieldIsEmptyMessage,
                },
                validate: (formValue) => {
                  const bearerPrefix = 'bearer ';

                  if (selectedAuthorizationType !== 'none') {
                    // We want to remove the automatically added bearer prefix
                    // to see if the input field is actually empty
                    return getEmptyInputMessage(
                      formValue?.toLowerCase()?.startsWith(bearerPrefix)
                        ? formValue.slice(bearerPrefix.length)
                        : formValue,
                    );
                  }
                  return undefined;
                },
              }}
              render={({
                field: { onChange },
                fieldState: { error },
                formState: { defaultValues },
              }) => (
                <AuthorizationHeader
                  disabled={disabled}
                  value={defaultValues?.authorizationHeader}
                  onChange={onChange}
                  selectedAuthorizationType={selectedAuthorizationType}
                  setSelectedAuthorizationType={setSelectedAuthorizationType}
                  meta={{
                    error: error?.message,
                  }}
                />
              )}
            />
          </FormField>
        </>
      )}

      {selectedAuthorizationMethod?.id === 'oAuth' && (
        <Controller
          name={'request.o_auth_connection_id' as Path<TFieldValues>}
          control={control}
          rules={{
            required: {
              value: true,
              message:
                'Select a strategy, fill out the required fields and save the configuration',
            },
          }}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <OAuth
              disabled={disabled}
              parentOAuthId={requestOAuthId}
              onChangeParentOAuthId={onChange}
              onChangeOAuthStrategy={onChangeOAuthStrategy}
              meta={{
                error: error?.message,
              }}
              handleParentSubmit={handleCustomCardSubmit}
            />
          )}
        />
      )}

      <FormField>
        <Controller
          name={'customHeaders' as Path<TFieldValues>}
          control={control}
          rules={{
            validate: (formValues) => isValidateCustomHeaders(formValues),
          }}
          render={({ field: { onChange }, formState: { defaultValues } }) => (
            <CustomHeaders
              disabled={disabled}
              onChange={onChange}
              value={defaultValues?.customHeaders}
            />
          )}
        />
      </FormField>
    </FormGrid>
  );
};
